/**
 * FERP Portal - Floor Plan Auto-Extraction Hook
 *
 * DRR-179 Phase 3: Calls Claude vision API via the agent service to identify
 * room labels and positions from an uploaded floor plan image.
 *
 * Returns suggested placements for admin review before saving.
 *
 * Usage:
 * ```tsx
 * const { suggest, suggestions, loading } = useFloorPlanAutoExtract();
 * await suggest(documentId, 'assisted_living');
 * // suggestions = [{ label: 'Room 101', x: 0.15, y: 0.42, confidence: 0.9 }]
 * ```
 */

import { useState, useCallback } from 'react';
import { gql } from '@apollo/client/core';
import { useMutation } from '@apollo/client/react';

const SUGGEST_ROOM_PLACEMENTS = gql`
  mutation SuggestRoomPlacements($input: SuggestRoomPlacementsInput!) {
    suggestRoomPlacements(input: $input) {
      suggestions {
        label
        x
        y
        confidence
        roomType
      }
      usage {
        inputTokens
        outputTokens
      }
      processingTimeMs
    }
  }
`;

export interface RoomSuggestion {
  label: string;
  x: number;
  y: number;
  confidence: number;
  roomType?: string;
}

interface SuggestRoomPlacementsData {
  suggestRoomPlacements: {
    suggestions: Array<{
      label: string;
      x: number;
      y: number;
      confidence: number;
      roomType: string | null;
    }>;
    usage: { inputTokens: number; outputTokens: number };
    processingTimeMs: number;
  };
}

export interface UseFloorPlanAutoExtractReturn {
  /** Call to suggest room placements from a floor plan document */
  suggest: (documentId: string, industry?: string) => Promise<RoomSuggestion[]>;
  /** Last set of suggestions returned */
  suggestions: RoomSuggestion[];
  /** Whether extraction is in progress */
  loading: boolean;
  /** Error message if extraction failed */
  error: string | null;
  /** Processing time of last extraction (ms) */
  processingTimeMs: number | null;
}

export function useFloorPlanAutoExtract(): UseFloorPlanAutoExtractReturn {
  const [suggestions, setSuggestions] = useState<RoomSuggestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [processingTimeMs, setProcessingTimeMs] = useState<number | null>(null);

  const [mutate, { loading }] = useMutation<SuggestRoomPlacementsData>(SUGGEST_ROOM_PLACEMENTS);

  const suggest = useCallback(
    async (documentId: string, industry?: string): Promise<RoomSuggestion[]> => {
      try {
        setError(null);
        setSuggestions([]);
        setProcessingTimeMs(null);

        const result = await mutate({
          variables: {
            input: {
              floorPlanDocumentId: documentId,
              industry: industry ?? null,
            },
          },
        });

        const data = result.data?.suggestRoomPlacements;
        if (!data) {
          setError('No response from extraction service');
          return [];
        }

        setProcessingTimeMs(data.processingTimeMs);

        const mapped: RoomSuggestion[] = data.suggestions.map((s) => ({
          label: s.label,
          x: s.x,
          y: s.y,
          confidence: s.confidence,
          roomType: s.roomType ?? undefined,
        }));

        setSuggestions(mapped);
        return mapped;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Floor plan extraction failed';
        setError(msg);
        return [];
      }
    },
    [mutate],
  );

  return { suggest, suggestions, loading, error, processingTimeMs };
}
